// src/pages/mantenimiento/TabKpis.jsx
// Separado en 2 secciones bien marcadas, cada una con SU PROPIO filtro,
// para que quede claro qué número depende de qué rango.
import { useEffect, useState, useRef } from 'react';
import Chart from 'chart.js/auto';
import api from '../../services/api';
import styles from './estilos';
import { FiltroFecha, numeroSemanaActual, primerDiaMesActual } from './componentes';

function TabKpis() {
  const [resumen, setResumen] = useState(null);
  const [planResumen, setPlanResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fDesde, setFDesde] = useState(primerDiaMesActual());
  const [fHasta, setFHasta] = useState(new Date().toISOString().slice(0, 10));
  const [horasSemana, setHorasSemana] = useState('');
  const [guardandoConfig, setGuardandoConfig] = useState(false);
  const [anioPlan, setAnioPlan] = useState(new Date().getFullYear());
  const [semanaDesdePlan, setSemanaDesdePlan] = useState(1);
  const [semanaHastaPlan, setSemanaHastaPlan] = useState(numeroSemanaActual());
  const canvasTipoRef = useRef(null);
  const chartTipoRef = useRef(null);
  const canvasCostoRef = useRef(null);
  const chartCostoRef = useRef(null);
  const [costoResumen, setCostoResumen] = useState(null);

  async function cargarTickets() {
    const res = await api.get('/api/mantenimiento/kpis-resumen', { params: { desde: fDesde, hasta: fHasta } });
    setResumen(res.data.data);
  }
  async function cargarPlan() {
    const res = await api.get('/api/mantenimiento/plan-resumen', { params: { anio: anioPlan, semanaDesde: semanaDesdePlan, semanaHasta: semanaHastaPlan } });
    setPlanResumen(res.data.data);
  }

  async function cargarCosto() {
    const res = await api.get('/api/mantenimiento/costos/resumen', { params: { desde: fDesde, hasta: fHasta } });
    setCostoResumen({ datos: res.data.data, total: res.data.costo_total_general });
  }

  async function cargarTodo() {
    try {
      setCargando(true);
      const [, , resConfig] = await Promise.all([cargarTickets(), cargarPlan(), cargarCosto(), api.get('/api/mantenimiento/config/tiempo-operativo')]);
      const planta = resConfig.data.data.find((r) => r.referencia === 'planta');
      setHorasSemana(planta ? planta.horas_semana : 80);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }
  useEffect(() => { cargarTodo(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canvasCostoRef.current || !costoResumen?.datos?.length) return;
    if (chartCostoRef.current) chartCostoRef.current.destroy();
    const datosOrdenados = [...costoResumen.datos].sort((a, b) => parseFloat(b.costo_total) - parseFloat(a.costo_total));
    chartCostoRef.current = new Chart(canvasCostoRef.current.getContext('2d'), {
      type: 'bar',
      data: {
        labels: datosOrdenados.map((d) => d.departamento),
        datasets: [{
          label: 'Costo ($)',
          data: datosOrdenados.map((d) => parseFloat(d.costo_total)),
          backgroundColor: datosOrdenados.map((_, i) => (i === 0 ? '#B23A48' : '#A98BB8')),
          borderRadius: 6,
        }],
      },
      options: {
        indexAxis: 'y',
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { x: { beginAtZero: true, ticks: { callback: (v) => '$' + v.toLocaleString('es-MX') } } },
      },
    });
    return () => { if (chartCostoRef.current) chartCostoRef.current.destroy(); };
  }, [costoResumen]);

  useEffect(() => {
    if (!canvasTipoRef.current || !resumen?.por_tipo?.length) return;
    if (chartTipoRef.current) chartTipoRef.current.destroy();
    const colores = { Preventivo: '#3E7C5A', Correctivo: '#B23A48' };
    chartTipoRef.current = new Chart(canvasTipoRef.current.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: resumen.por_tipo.map((t) => t.tipo),
        datasets: [{
          data: resumen.por_tipo.map((t) => t.total),
          backgroundColor: resumen.por_tipo.map((t) => colores[t.tipo] || '#8A7690'),
          borderColor: '#fff', borderWidth: 3,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false, cutout: '60%',
        plugins: { legend: { position: 'right', labels: { font: { size: 11 } } } },
      },
    });
    return () => { if (chartTipoRef.current) chartTipoRef.current.destroy(); };
  }, [resumen]);

  async function handleGuardarConfig() {
    try {
      setGuardandoConfig(true);
      await api.put('/api/mantenimiento/config/tiempo-operativo/planta', { horas_semana: horasSemana });
      cargarTickets();
    } catch (err) {
      console.error(err);
    } finally {
      setGuardandoConfig(false);
    }
  }

  if (cargando || !resumen) return <div>Cargando...</div>;

  const semaforoPct = (v, meta) => (v === null || v === undefined ? '#8A7690' : v >= meta ? '#3E7C5A' : v >= meta - 15 ? '#C08A2B' : '#B23A48');

  return (
    <div>
      {/* ============ SECCIÓN 1: PLAN PREVENTIVO ============ */}
      <div style={styles.seccionKpiBloque}>
        <div style={styles.seccionKpiHeader}>
          <div style={styles.seccionKpiTitulo}>3.1 — Cumplimiento del Plan Preventivo</div>
          <div style={styles.seccionKpiFormula}>
            % = (equipos programados esa semana que ya tienen un <strong>Ticket Preventivo cerrado</strong>) ÷ (equipos programados esa semana) — sale de la pestaña "Plan Mensual"
          </div>
        </div>

        <div style={{ ...styles.kgrid, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <div style={{ ...styles.kcard, borderTop: `3px solid ${semaforoPct(planResumen?.pct_cumplimiento, 95)}` }}>
            <div style={styles.kLbl}>% Cumplimiento</div>
            <div style={{ ...styles.kVal, color: semaforoPct(planResumen?.pct_cumplimiento, 95) }}>
              {planResumen?.pct_cumplimiento !== null && planResumen?.pct_cumplimiento !== undefined ? `${planResumen.pct_cumplimiento}%` : '—'}
            </div>
            <div style={styles.kSub}>{planResumen?.realizados ?? 0} de {planResumen?.programados ?? 0} programados con ticket cerrado</div>
          </div>
        </div>

        <div style={styles.filtrosBar}>
          <div style={styles.filtroGrupo}>
            <label style={styles.filtroLabel}>Año</label>
            <input type="number" value={anioPlan} onChange={(e) => setAnioPlan(e.target.value)} style={{ ...styles.filtroInput, width: '80px' }} />
          </div>
          <div style={styles.filtroGrupo}>
            <label style={styles.filtroLabel}>Semana desde</label>
            <input type="number" value={semanaDesdePlan} onChange={(e) => setSemanaDesdePlan(e.target.value)} style={{ ...styles.filtroInput, width: '70px' }} />
          </div>
          <div style={styles.filtroGrupo}>
            <label style={styles.filtroLabel}>Semana hasta</label>
            <input type="number" value={semanaHastaPlan} onChange={(e) => setSemanaHastaPlan(e.target.value)} style={{ ...styles.filtroInput, width: '70px' }} />
          </div>
          <button onClick={cargarPlan} style={styles.botonBuscar2}>Buscar</button>
        </div>
      </div>

      {/* ============ SECCIÓN 2: TICKETS (F-MTO-03) ============ */}
      <div style={{ ...styles.seccionKpiBloque, marginTop: 24 }}>
        <div style={styles.seccionKpiHeader}>
          <div style={styles.seccionKpiTitulo}>3.2 / 3.3 / 3.4 / 3.5 / 3.6 — Indicadores de Tickets (F-MTO-03)</div>
          <div style={styles.seccionKpiFormula}>
            Se calculan sobre los <strong>tickets reportados</strong> dentro del rango de fechas de abajo. 3.2 = tickets cerrados ÷ tickets creados en el rango.
          </div>
        </div>

        <div style={styles.kgrid}>
          <div style={{ ...styles.kcard, borderTop: `3px solid ${semaforoPct(resumen.cumplimiento_ticket.pct_cumplimiento, 90)}` }}>
            <div style={styles.kLbl}>3.2 % Cumplimiento de Ticket</div>
            <div style={{ ...styles.kVal, color: semaforoPct(resumen.cumplimiento_ticket.pct_cumplimiento, 90) }}>
              {resumen.cumplimiento_ticket.pct_cumplimiento !== null ? `${resumen.cumplimiento_ticket.pct_cumplimiento}%` : '—'}
            </div>
            <div style={styles.kSub}>
              {resumen.cumplimiento_ticket.total_cerrados} de {resumen.cumplimiento_ticket.total_creados} cerrados · {resumen.cumplimiento_ticket.dias_promedio_abierto} días en promedio abierto
            </div>
          </div>
          <div style={styles.kcard}>
            <div style={styles.kLbl}>MTTR — Tiempo Medio de Reparación</div>
            <div style={styles.kVal}>{resumen.mttr} hrs</div>
            <div style={styles.kSub}>promedio de (hora de cierre − hora de reporte) · {resumen.num_tickets_cerrados} tickets cerrados en el rango</div>
          </div>
          <div style={styles.kcard}>
            <div style={styles.kLbl}>MTBF — Tiempo Medio Entre Fallas</div>
            <div style={styles.kVal}>{resumen.mtbf !== null ? `${resumen.mtbf} hrs` : '—'}</div>
            <div style={styles.kSub}>horas operativas del rango ÷ {resumen.num_fallas_correctivas} fallas correctivas</div>
          </div>
          <div style={{ ...styles.kcard, borderTop: `3px solid ${semaforoPct(resumen.confiabilidad, 90)}` }}>
            <div style={styles.kLbl}>Confiabilidad</div>
            <div style={{ ...styles.kVal, color: semaforoPct(resumen.confiabilidad, 90) }}>{resumen.confiabilidad !== null ? `${resumen.confiabilidad}%` : '—'}</div>
            <div style={styles.kSub}>(horas operativas − horas de paro) ÷ horas operativas · {resumen.tiempo_total_operativo} hrs operativas en el rango</div>
          </div>
          <div style={styles.kcard}>
            <div style={styles.kLbl}>3.6 Costo de Mantenimiento</div>
            <div style={styles.kVal}>${(costoResumen?.total ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</div>
            <div style={styles.kSub}>{costoResumen?.datos?.length ?? 0} departamento(s) con gasto en el rango</div>
          </div>
        </div>

        {costoResumen?.datos?.length > 0 && (
          <div style={{ ...styles.tarjeta, marginTop: 14 }}>
            <div style={styles.seccionTitulo}>Top Departamentos con Más Gasto</div>
            <div style={{ position: 'relative', height: Math.max(140, costoResumen.datos.length * 40) }}>
              <canvas ref={canvasCostoRef} />
            </div>
          </div>
        )}

        {resumen.por_tipo?.length > 0 && (
          <div style={{ ...styles.tarjeta, marginTop: 14 }}>
            <div style={styles.seccionTitulo}>Tickets por Tipo (Preventivo vs Correctivo)</div>
            <div style={{ position: 'relative', height: 200 }}>
              <canvas ref={canvasTipoRef} />
            </div>
          </div>
        )}

        {resumen.cumplimiento_ticket.mas_tardados.length > 0 && (
          <div style={{ ...styles.tarjeta, marginTop: 14 }}>
            <div style={styles.seccionTitulo}>Tickets que más tardaron abiertos (en el rango)</div>
            <div style={styles.tablaScroll}>
              <table style={styles.tabla}>
                <thead><tr><th style={styles.th}>Folio</th><th style={styles.th}>Equipo</th><th style={styles.th}>Reportado</th><th style={styles.th}>Cerrado</th><th style={styles.th}>Días abierto</th></tr></thead>
                <tbody>
                  {resumen.cumplimiento_ticket.mas_tardados.map((t, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{t.folio ? `#${t.folio}` : '—'}</td>
                      <td style={styles.td}>{t.equipo}</td>
                      <td style={styles.td}>{new Date(t.hora_reporte).toLocaleDateString('es-MX')}</td>
                      <td style={styles.td}>{new Date(t.hora_cierre).toLocaleDateString('es-MX')}</td>
                      <td style={{ ...styles.td, fontWeight: 700, color: t.dias > 3 ? '#B23A48' : '#3A2E42' }}>{t.dias} días</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={styles.filtrosBar}>
          <FiltroFecha label="Desde" value={fDesde} onChange={setFDesde} />
          <FiltroFecha label="Hasta" value={fHasta} onChange={setFHasta} />
          <button onClick={() => { cargarTickets(); cargarCosto(); }} style={styles.botonBuscar2}>Buscar</button>
        </div>

        <div style={styles.tarjeta}>
          <div style={styles.seccionTitulo}>Tiempo Total Operativo (referencia para MTBF y Confiabilidad)</div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input type="number" step="0.01" value={horasSemana} onChange={(e) => setHorasSemana(e.target.value)} style={{ ...styles.input, width: '110px' }} />
            <span style={{ fontSize: '12px', color: '#8A7690' }}>hrs / semana (toda la planta) — se multiplica por el número de semanas del rango de arriba</span>
            <button onClick={handleGuardarConfig} disabled={guardandoConfig} style={styles.botonGuardarChico}>
              {guardandoConfig ? '...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TabKpis;